// src/services/customerService.js

import apiClient from '../api/apiClient';

/**
 * Fetch all customers with pagination support.
 * @param {string} orgId - The organization ID.
 * @param {Object} options - Pagination and filtering options.
 * @returns {Promise<Object>} Promise resolving to paginated customers data.
 */
export const getCustomers = async (orgId, options = {}) => {
  const intOrgId = parseInt(orgId, 10);
  const params = new URLSearchParams();
  
  // Add pagination parameters if provided
  if (options.page) params.append('page', options.page);
  if (options.limit) params.append('limit', options.limit);
  if (options.search !== undefined && options.search !== null) params.append('search', options.search);
  if (options.sortBy) params.append('sortBy', options.sortBy);
  if (options.sortOrder) params.append('sortOrder', options.sortOrder);
  if (options.dateFrom) params.append('dateFrom', options.dateFrom);
  if (options.dateTo) params.append('dateTo', options.dateTo);
  
  const queryString = params.toString();
  const url = `/customers/org/${intOrgId}${queryString ? `?${queryString}` : ''}`;
  
  return apiClient.get(url)
    .then(response => response.data);
};

/**
 * Add a new customer.
 * @param {Object} data - The customer data.
 * @returns {Promise<void>}
 */
export const addCustomer = async (orgId, data) => {
  const intOrgId = parseInt(orgId, 10);
  // Ensure whatsapp_number is in meta
  let payload = { ...data };
  if (data.whatsapp_number) {
    payload.meta = { ...(data.meta || {}), whatsapp_number: data.whatsapp_number };
    delete payload.whatsapp_number;
  }
  return apiClient.post('/customers', { ...payload, org_id: intOrgId })
    .then(response => response.data);
};

/**
 * Find a customer by ID.
 * @param {string} customerId - The customer ID to search for.
 * @returns {Promise<Object|null>} Promise resolving to the found customer or null.
 */
export const getCustomerById = async (orgId, customerId) => {
  return apiClient.get(`/customers/${customerId}`)
    .then(response => response.data);
};

/**
 * Update an existing customer.
 * @param {string} customerId - The customer ID to update.
 * @param {Object} data - The new data for the customer.
 * @returns {Promise<Object>} Promise resolving to the updated customer data.
 */
export const updateCustomer = async (orgId, customerId, data) => {
  const intOrgId = parseInt(orgId, 10);
  let payload = { ...data };
  if (data.whatsapp_number) {
    payload.meta = { ...(data.meta || {}), whatsapp_number: data.whatsapp_number };
    delete payload.whatsapp_number;
  }
  return apiClient.put(`/customers/${customerId}`, { ...payload, org_id: intOrgId })
    .then(response => response.data);
};

/**
 * Delete multiple customers.
 * @param {Array<string>} customerIds - The list of customer IDs to delete.
 * @returns {Promise<void>}
 */
export const deleteMultipleCustomers = (customerIds) => {
  return apiClient.post('/customers/bulk-delete', { customer_ids: customerIds })
    .then(response => response.data);
};

/**
 * Bulk create customers.
 * @param {Object} data - The customer data for bulk creation.
 * @returns {Promise<void>}
 */
export const bulkCreateCustomers = (data) => {
  // Each item in data should have meta.whatsapp_number if present
  return apiClient.post('/customers/bulk', data).then(response => response.data);
};

